<template>
  <div class="container py-4">
    <h1 class="mb-4">Design Studio</h1>

    <div v-if="loadingSubstrates" class="text-center">
      <b-spinner label="Loading substrates..."></b-spinner>
    </div>

    <div v-else class="mb-5">
      <div class="mb-4">
        <b-button @click="fetchTestImage" variant="outline-primary">Load Test Image</b-button>
        <div v-if="selectedImage" class="mt-3">
          <b-img :src="selectedImage.thumb" thumbnail fluid alt="Selected Image" style="max-height: 200px;"></b-img>
          <p class="small text-muted mt-1">{{ selectedImage.title }}</p>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 mb-3">
           <b-form-group label="Workspace/Project">
             <QuickAddWorkspace
               v-model="selectedWorkspace"
               :profile="profile"
             />
           </b-form-group>
        </div>
        <div class="col-md-6 mb-3">
          <b-form-group label="Dimensions (inches)">
            <div class="d-flex align-items-center">
              <b-form-input
                v-model="dimensions.width"
                type="number"
                placeholder="Width"
                class="mr-2"
                :state="getFieldError('config_data.width', 'width') ? false : null"
              ></b-form-input>
              <span class="mr-2">x</span>
              <b-form-input
                v-model="dimensions.height"
                type="number"
                placeholder="Height"
                :state="getFieldError('config_data.height', 'height') ? false : null"
              ></b-form-input>
            </div>
            <b-form-invalid-feedback :state="getFieldError('config_data.width', 'width') ? false : null">
              {{ getFieldError('config_data.width', 'width') }}
            </b-form-invalid-feedback>
            <b-form-invalid-feedback :state="getFieldError('config_data.height', 'height') ? false : null">
              {{ getFieldError('config_data.height', 'height') }}
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
      </div>

      <b-form-group label="Select Substrate">
        <b-form-select v-model="selectedSubstrate" :options="substrateOptions">
          <template #first>
            <b-form-select-option :value="null" disabled>-- Select a substrate --</b-form-select-option>
          </template>
        </b-form-select>
      </b-form-group>
    </div>

    <div v-if="selectedSubstrate">
      <div v-if="loadingOptions" class="text-center">
        <b-spinner label="Loading options..."></b-spinner>
      </div>
      <div v-else-if="availableOptions.length === 0" class="alert alert-info">
        No options available for this substrate.
      </div>
      <div v-else>
        <div class="row">
          <div v-for="(options, category) in groupedOptions" :key="category" class="col-md-4 mb-4">

            <!-- Subcategory Dropdown (if applicable) -->
            <div v-if="getSubcategories(options).length > 0" class="mb-2">
              <b-form-group :label="category + ' Family'">
                <b-form-select v-model="selectedSubcategories[category]" :options="getSubcategories(options)">
                  <template #first>
                    <b-form-select-option :value="null">-- All {{ category }} Families --</b-form-select-option>
                  </template>
                </b-form-select>
              </b-form-group>
            </div>

            <!-- Main Options Dropdown -->
            <b-form-group
              :label="category"
              :invalid-feedback="getOptionError(category)"
              :state="getOptionError(category) ? false : null"
            >
              <b-form-select
                v-model="selectedOptions[category]"
                :options="getCategoryOptionsFiltered(options, category)"
                :state="getOptionError(category) ? false : null"
              >
                <template #first>
                  <b-form-select-option :value="null">-- Select {{ category }} --</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </div>
        </div>
      </div>
    </div>

    <div v-if="selectedSubstrate" class="mt-4">
      <h4>Item Data</h4>
      <b-form-textarea
        :value="JSON.stringify(itemData, null, 2)"
        rows="10"
        readonly
        style="font-family: monospace;"
        class="mb-3"
      ></b-form-textarea>
    </div>
    <div
      v-if="selectedSubstrate"
      class="sticky-bottom frosted eat-gutter py-3 bottom-bar"
    >
      <div class="container d-flex align-items-center justify-content-end">
        <div v-if="errorCount" class="mr-3 d-flex align-items-center">
          <b-popover
            target="design-studio-errors"
            triggers="hover click blur"
            placement="top"
          >
            <ul class="mb-0 pl-3" style="max-width: 400px;">
              <li v-for="(msg, key) in validationErrors" :key="key">
                <strong>{{ key }}:</strong> {{ msg }}
              </li>
            </ul>
          </b-popover>
          <span
            id="design-studio-errors"
            class="text-danger d-flex align-items-center"
            style="cursor: pointer;"
          >
            <IconX width="1em" height="1em" class="fill-color mr-1" />
            {{ errorCount }} {{ errorCount === 1 ? 'error' : 'errors' }}
          </span>
        </div>

        <div v-if="priceData" class="d-flex align-items-center gap-3 mr-3">
          <b-popover
            target="design-studio-price"
            triggers="hover click blur"
            placement="top"
          >
            <table class="table table-sm table-borderless mb-0" style="font-size: 0.85rem;">
              <tbody>
                <template v-if="priceData.itemized">
                  <tr>
                    <td class="text-muted pr-3" colspan="2">Itemized</td>
                  </tr>
                  <tr v-for="(val, key) in priceData.itemized" :key="'iz-'+key">
                    <td class="text-muted pr-3 pl-3">{{ key }}</td>
                    <td class="text-right text-nowrap">${{ formatPrice(val) }}</td>
                  </tr>
                </template>
                <tr v-if="priceData.unit_sell_price">
                  <td class="text-muted pr-3">Unit Sell</td>
                  <td class="text-right text-nowrap">${{ formatPrice(priceData.unit_sell_price) }}</td>
                </tr>
                <tr v-if="priceData.unit_net_price">
                  <td class="text-muted pr-3">Unit Net</td>
                  <td class="text-right text-nowrap">${{ formatPrice(priceData.unit_net_price) }}</td>
                </tr>
                <tr v-if="priceData.quantity">
                  <td class="text-muted pr-3">Qty</td>
                  <td class="text-right text-nowrap">{{ priceData.quantity }}</td>
                </tr>
                <tr v-if="priceData.total_sell_price" class="font-weight-bold">
                  <td class="pr-3">Total Sell</td>
                  <td class="text-right text-nowrap">${{ formatPrice(priceData.total_sell_price) }}</td>
                </tr>
                <tr v-if="priceData.total_net_price" class="font-weight-bold">
                  <td class="pr-3">Total Net</td>
                  <td class="text-right text-nowrap">${{ formatPrice(priceData.total_net_price) }}</td>
                </tr>
              </tbody>
            </table>
          </b-popover>
          <IconCheck
            v-if="priceCommitted"
            width="1em"
            height="1em"
            class="fill-color text-success"
          />
          <span
            id="design-studio-price"
            :class="{'text-muted': pricingLoading}"
            style="cursor: pointer;"
          >
            ${{ formatPrice(priceData.total_sell_price || priceData.discounted_sell_price || priceData.clean_sell_price || '—') }}
          </span>
        </div>

        <LoadingButton
          variant="primary"
          @click="saveDesign"
          :busy="saving"
          :disabled="saving"
        >
          Save Design
        </LoadingButton>
      </div>
    </div>
    <div v-if="selectedSubstrate" class="mb-bar"></div>
  </div>
</template>

<script>
import { http, http2, getAppApi } from '@/services/http.js'
import { groupBy, debounce } from 'lodash'
import QuickAddWorkspace from '@/components/QuickAddWorkspace.vue'
import LoadingButton from '@/components/LoadingButton.vue'
import { mapState } from 'vuex'
import IconCheck from '@primer/octicons/build/svg/check-16.svg'
import IconX from '@primer/octicons/build/svg/x-circle-fill-16.svg'

export default {
  name: 'DesignStudio',
  components: {
    QuickAddWorkspace,
    LoadingButton,
    IconCheck,
    IconX,
  },
  data() {
    return {
      substrates: [],
      selectedSubstrate: null,
      availableOptions: [],
      selectedOptions: {},
      selectedSubcategories: {}, // Store selected subcategory for each category
      selectedImage: null,
      selectedWorkspace: null,
      dimensions: {
        width: '',
        height: ''
      },
      loadingSubstrates: false,
      loadingOptions: false,
      saving: false,
      pendingChosenOptions: null,
      priceData: null,
      priceCommitted: false,
      pricingLoading: false,
      loadingItem: false,
      validationErrors: {},
    }
  },
  computed: {
    errorCount() {
      return Object.keys(this.validationErrors).length
    },
    substrateOptions() {
      return this.substrates.map(s => ({
        value: s.id,
        text: s.name
      }))
    },
    groupedOptions() {
      // Group by category name (field 'name' in serializer)
      return groupBy(this.availableOptions, 'name')
    },
    currentSubstrateName() {
      const sub = this.substrates.find(s => s.id === this.selectedSubstrate)
      return sub ? sub.name : ''
    },
    itemData() {
      if (!this.selectedSubstrate) return {}

      const chosenOptions = []

      Object.entries(this.selectedOptions).forEach(([category, optionId]) => {
        if (!optionId) return

        // Find the option object in availableOptions
        const option = this.availableOptions.find(o => o.id === optionId)
        if (option) {
          chosenOptions.push({
            option_id: optionId,
            name: category,
            value: option.value || option.name
          })
        }
      })

      return {
        imagedata: this.selectedImage,
        // API now expects width/height only on config_data;
        // size is read-only and will be returned by the API.
        // API expects workspace as a string name and project_id as int
        project_id: this.selectedWorkspace?.project || this.profile?.current_project?.id || null,
        workspace: this.selectedWorkspace?.name || this.selectedWorkspace?.label || '',
        quantity: 1,
        config_data: {
          substrate: this.currentSubstrateName,
          width: this.dimensions.width,
          height: this.dimensions.height,
          chosen_options: chosenOptions
        }
      }
    },
    ...mapState('user', ['profile'])
  },
  watch: {
    selectedSubstrate(newVal) {
      this.selectedOptions = {}
      this.selectedSubcategories = {}
      if (newVal) {
        this.fetchOptions(newVal)
      } else {
        this.availableOptions = []
      }
      if (!this.loadingItem) {
        this.debouncedUpdatePriceData()
      }
    },
    '$route.query.item': {
      handler(itemId) {
        if (itemId) {
          // For existing items, load from API (including any stored price_data)
          // and treat that as committed price until the user makes changes.
          this.loadItem(itemId)
        } else {
          // For new items, compute preview pricing as the user configures options.
          this.debouncedUpdatePriceData()
        }
      },
      immediate: true,
    },
    availableOptions: {
      handler(opts) {
        if (opts.length && this.pendingChosenOptions?.length) {
          const selected = {}
          this.pendingChosenOptions.forEach(co => {
            const opt = opts.find(o =>
              o.id === co.option_id || (o.name === co.name && String(o.value || o.name) === String(co.value))
            )
            if (opt) selected[opt.name] = opt.id
          })
          this.selectedOptions = { ...this.selectedOptions, ...selected }
          this.pendingChosenOptions = null
        }
      },
    },
    selectedOptions: {
      handler() {
        if (!this.loadingItem) {
          this.debouncedUpdatePriceData()
        }
      },
      deep: true,
    },
    dimensions: {
      handler() {
        if (!this.loadingItem) {
          this.debouncedUpdatePriceData()
        }
      },
      deep: true,
    },
    selectedWorkspace() {
      if (!this.loadingItem) {
        this.debouncedUpdatePriceData()
      }
    },
    selectedImage() {
      if (!this.loadingItem) {
        this.debouncedUpdatePriceData()
      }
    },
  },
  created() {
    this.debouncedUpdatePriceData = debounce(this.updatePriceData, 200)
    this.fetchSubstrates()
  },
  methods: {
    formatPrice(val) {
      const n = parseFloat(val)
      return isNaN(n) ? val : n.toFixed(2)
    },
    parseValidationErrors(e) {
      const data = e && e.response && e.response.data
      if (!data || typeof data !== 'object') return {}
      // Flatten nested errors like { config_data: { width: [...] } }
      // into { 'config_data.width': 'message' }
      const errors = {}
      const chosenOptions = this.itemData?.config_data?.chosen_options || []
      const flatten = (obj, prefix) => {
        Object.entries(obj).forEach(([key, val]) => {
          const path = prefix ? `${prefix}.${key}` : key
          if (Array.isArray(val)) {
            // Array of strings = simple error list
            if (val.length && typeof val[0] === 'string') {
              errors[path] = val.join(', ')
            } else {
              // Array of objects (e.g. chosen_options per-index errors)
              val.forEach((item, idx) => {
                if (item && typeof item === 'object' && Object.keys(item).length) {
                  // Try to use the option name for a friendlier key
                  const optionName = chosenOptions[idx]?.name || `#${idx}`
                  const label = prefix ? `${prefix}.${optionName}` : `${key}.${optionName}`
                  flatten(item, label)
                }
              })
            }
          } else if (val && typeof val === 'object') {
            flatten(val, path)
          } else if (typeof val === 'string') {
            errors[path] = val
          }
        })
      }
      flatten(data, '')
      return errors
    },
    getFieldError(...keys) {
      for (const key of keys) {
        if (this.validationErrors[key]) return this.validationErrors[key]
      }
      return null
    },
    getOptionError(category) {
      // Match errors keyed like config_data.CategoryName.field
      const prefix = `config_data.${category}.`
      const msgs = []
      Object.entries(this.validationErrors).forEach(([key, msg]) => {
        if (key.startsWith(prefix) || key === `config_data.${category}`) {
          msgs.push(msg)
        }
      })
      return msgs.length ? msgs.join(', ') : null
    },
    async updatePriceData() {
      // Don't run if we don't have a valid substrate selection yet
      if (!this.selectedSubstrate) {
        this.priceData = null
        this.priceCommitted = false
        return
      }
      try {
        const payload = this.itemData
        if (!Object.keys(payload || {}).length) {
          this.priceData = null
          this.priceCommitted = false
          return
        }
        const itemId = this.$route.query.item
        const url = itemId
          ? `api/v3/design-studio/items/${itemId}/`
          : 'api/v3/design-studio/items/'

        this.pricingLoading = true

        const response = itemId
          ? await http2.patch(
              url,
              payload,
              { params: { dry_run: true } }
            )
          : await http2.post(
              url,
              payload,
              { params: { dry_run: true } }
            )

        this.validationErrors = {}

        const newPriceData = response.data && response.data.price_data ? response.data.price_data : null
        // If the dry-run price matches what we already have committed, keep the checkmark
        if (this.priceCommitted && this.priceData && newPriceData &&
            JSON.stringify(this.priceData) === JSON.stringify(newPriceData)) {
          this.priceData = newPriceData
        } else {
          this.priceData = newPriceData
          this.priceCommitted = false
        }
      } catch (e) {
        // On error, clear price so we don't show stale data
        this.priceData = null
        this.priceCommitted = false
        this.validationErrors = this.parseValidationErrors(e)
      } finally {
        this.pricingLoading = false
      }
    },
    async loadItem(itemId) {
      if (!itemId) return
      try {
        this.loadingItem = true
        if (!this.substrates.length) {
          await this.fetchSubstrates()
        }

        const response = await http2.get(`api/v3/design-studio/items/${itemId}/`)
        const item = response.data

        this.selectedImage = item.imagedata || null
        this.dimensions = {
          width: String(item.width ?? item.config_data?.width ?? ''),
          height: String(item.height ?? item.config_data?.height ?? ''),
        }

        const substrateName = item.substrate || item.config_data?.substrate
        if (substrateName && this.substrates.length) {
          const sub = this.substrates.find(s => s.name?.toLowerCase() === String(substrateName).toLowerCase())
          this.selectedSubstrate = sub ? sub.id : null
          this.pendingChosenOptions = item.config_data?.chosen_options || null
        }

        const projectId = typeof item.project === 'object' ? item.project?.id : item.project
        if (projectId && item.workspace) {
          await this.loadWorkspaceForItem(projectId, item.workspace)
        }

        // If the item already has price_data, use it as the expected/committed price.
        if (item.price_data) {
          this.priceData = item.price_data
          this.priceCommitted = true
        } else {
          this.priceData = null
          this.priceCommitted = false
        }
      } catch (e) {
        console.error('Failed to load item', e)
        this.$bvToast.toast('Failed to load item', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loadingItem = false
      }
    },
    async loadWorkspaceForItem(projectId, workspaceName) {
      try {
        const response = await getAppApi().get('/api/v2/myworkspaces/', {
          params: { page_size: 100 },
        })
        const results = response.data?.results || []
        const pid = Number(projectId)
        const ws = results.find(w =>
          Number(w.project) === pid && (w.name === workspaceName || w.label?.includes(workspaceName))
        )
        if (ws) this.selectedWorkspace = ws
      } catch {
        // Ignore - user can re-select workspace
      }
    },
    getSubcategories(options) {
      const groups = new Set()
      options.forEach(opt => {
        const cGroups = opt.extra_data && opt.extra_data.color_groups
        if (Array.isArray(cGroups)) {
          cGroups.forEach(g => groups.add(g))
        }
      })
      return Array.from(groups).sort().map(g => ({ value: g, text: g }))
    },
    getCategoryOptionsFiltered(options, category) {
      const selectedSub = this.selectedSubcategories[category]
      let filteredOptions = options

      if (selectedSub) {
        filteredOptions = options.filter(opt => {
          const cGroups = opt.extra_data && opt.extra_data.color_groups
          return Array.isArray(cGroups) && cGroups.includes(selectedSub)
        })
      }

      // Return simple mapped options since filtering is handled by the subcategory select
      return filteredOptions.map(opt => ({
        value: opt.id,
        text: opt.value || opt.name
      }))
    },
    // getCategoryOptions removed as it's replaced by getCategoryOptionsFiltered
    async fetchTestImage() {
      try {
        const response = await http.get('content/search/v3/', {
          params: { limit: 1 }
        })
        if (response.data.images && response.data.images.length > 0) {
          this.selectedImage = response.data.images[0]
        }
      } catch (e) {
        console.error('Failed to fetch test image', e)
        this.$bvToast.toast('Failed to load test image', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      }
    },
    async fetchSubstrates() {
      this.loadingSubstrates = true
      try {
        const response = await http2.get('api/v1/substrate/')
        // Handle pagination or direct array
        this.substrates = Array.isArray(response.data) ? response.data : (response.data.results || [])
      } catch (e) {
        console.error('Failed to fetch substrates', e)
        this.$bvToast.toast('Failed to load substrates', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.loadingSubstrates = false
      }
    },
    async fetchOptions(substrateId) {
      this.loadingOptions = true
      try {
        const response = await http2.get('api/v3/design-studio/available-options/', {
          params: {
            substrate: substrateId,
            limit: 10000
          }
        })
        this.availableOptions = Array.isArray(response.data) ? response.data : (response.data.results || [])

        // Initialize selectedOptions with defaults or null
        const optionsByCategory = groupBy(this.availableOptions, 'name')
        const initialSelection = {}
        Object.keys(optionsByCategory).forEach(category => {
          const options = optionsByCategory[category]
          const defaultOpt = options.find(o => o.is_default)
          initialSelection[category] = defaultOpt ? defaultOpt.id : null
        })
        this.selectedOptions = initialSelection

      } catch (e) {
        console.error('Failed to fetch options', e)
        this.$bvToast.toast('Failed to load options', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
        this.availableOptions = []
      } finally {
        this.loadingOptions = false
      }
    },
    async saveDesign() {
      this.saving = true
      try {
        const payload = this.itemData
        const expectedPrice = this.priceData
        // Using http2 for main API
        const itemId = this.$route.query.item
        const url = itemId
          ? `api/v3/design-studio/items/${itemId}/`
          : 'api/v3/design-studio/items/'

        const response = itemId
          ? await http2.patch(
              url,
              payload,
              { params: { dry_run: false } }
            )
          : await http2.post(
              url,
              payload,
              { params: { dry_run: false } }
            )

        // Update local priceData from saved item, if present
        const savedPrice = response && response.data && response.data.price_data
        if (savedPrice) {
          this.priceData = savedPrice
        }

        // Assert that saved price matches last dry run price, if both exist
        if (expectedPrice && savedPrice) {
          const expectedJson = JSON.stringify(expectedPrice)
          const savedJson = JSON.stringify(savedPrice)
          if (expectedJson !== savedJson) {
            console.error('Design Studio price mismatch between dry run and saved item', {
              expected: expectedPrice,
              saved: savedPrice,
            })
            this.$bvToast.toast('Warning: saved price does not match previewed price.', {
              title: 'Pricing Mismatch',
              variant: 'warning',
              solid: true,
            })
            this.priceCommitted = false
          } else {
            this.priceCommitted = true
          }
        } else if (savedPrice) {
          // We only have a saved price, treat that as committed
          this.priceCommitted = true
        }

        // If we just created a new item, redirect to URL with its id
        if (!itemId && response && response.data && response.data.id) {
          this.$router.replace({
            path: '/design-studio',
            query: { item: response.data.id },
          })
        }

      } catch (e) {
        console.error('Failed to save design', e)
        this.$bvToast.toast('Failed to save design', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
